import { useEffect, useRef, useState, type FormEvent, type ReactNode } from "react";
import { ArrowRight, BriefcaseBusiness, CalendarDays, Check, ChevronLeft, PencilLine } from "lucide-react";
import { GROWTH_REQUESTS } from "../../../../config/Endpoints.js";
import { getAuthToken } from "../../../../utils/Storage.js";
import { Button } from "../../../components/ui/button/button.tsx";
import { Spinner } from "../../../components/ui/spinner/spinner.tsx";
import { DatePicker, DatePickerFieldTrigger } from "../../../components/ui/date-picker/date-picker.tsx";
import { TextArea } from "../../../components/ui/text-area/text-area.tsx";
import { postApi } from "../../../utils/api.ts";
import type { GrowthRequest } from "../growth-types.ts";
import LinkifiedText from "../linkified-text.tsx";
import {
  ANOTHER_PROJECT, MORE_RESPONSIBILITY, FROM_DATE, CONTRIBUTION_QUESTION_IDS,
  PROJECT_INFORMATION_NOTE, END_DATE_NOTE,
  availabilityQuestionId, contributionScenario, contributionToday, endingSoonProjects,
  contributionPayload, contributionReady, dateAnswer, parseProjectDate,
  projectDateLabel, questionOptions, type ContributionDraft, type CurrentProject,
} from "./contribution-data.ts";
import { useContributionContext } from "./use-contribution-context.ts";
import styles from "./contribution-form.module.css";

const EMPTY_DRAFT: ContributionDraft = { interest: "", availability: "", date: "", note: "", projectId: null };
const NOTE_SUGGESTIONS = ["Salesforce", "MuleSoft", "AI / Automation", "Technical leadership"];
const INTEREST_DESCRIPTIONS: Record<string, string> = {
  [ANOTHER_PROJECT]: "I’d like an additional assignment.",
  [MORE_RESPONSIBILITY]: "I’d like to do more within my current project.",
};

function ProjectDetails({ project, endingSoon = false }: { project: CurrentProject; endingSoon?: boolean }) {
  return <>
    <span className={`${styles.projectIcon} ${endingSoon ? styles.endingIcon : ""}`}><BriefcaseBusiness size={21} aria-hidden /></span>
    <span className={styles.projectCopy}>
      <span className={styles.projectNameRow}><strong>{project.name}</strong>{endingSoon ? <span className={styles.endingBadge}>ENDING SOON</span> : null}</span>
      <span>{project.role}</span>
      <small><CalendarDays size={14} aria-hidden />{projectDateLabel(project)}</small>
    </span>
  </>;
}

function Choice({ name, value, checked, onChange, children, project = false, compact = false }: {
  name: string; value: string; checked: boolean; onChange: () => void;
  children: ReactNode; project?: boolean; compact?: boolean;
}) {
  return <label className={`${styles.choice} ${checked ? styles.selected : ""} ${project ? styles.projectChoice : ""} ${compact ? styles.compactChoice : ""}`}>
    <input type="radio" name={name} value={value} checked={checked} onChange={onChange} />
    {!compact && !project ? <span className={styles.radioMark} aria-hidden>{checked ? <Check size={14} strokeWidth={3} /> : null}</span> : null}
    {children}
    {project ? <span className={styles.radioMark} aria-hidden>{checked ? <Check size={14} strokeWidth={3} /> : null}</span> : null}
  </label>;
}

export default function ContributionForm({ onBack, onSubmitted }: { onBack: () => void; onSubmitted: (request: GrowthRequest) => void }) {
  const { context, loading, error: loadError, retry } = useContributionContext();
  const [draft, setDraft] = useState<ContributionDraft>(EMPTY_DRAFT);
  const [reviewing, setReviewing] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [today] = useState(() => contributionToday());
  const submitLock = useRef(false);
  const heading = useRef<HTMLHeadingElement>(null);
  const reviewHeading = useRef<HTMLHeadingElement>(null);
  const noteInput = useRef<HTMLTextAreaElement>(null);

  useEffect(() => { if (!reviewing) heading.current?.focus(); }, [loading, reviewing]);
  useEffect(() => { if (reviewing) reviewHeading.current?.focus(); }, [reviewing]);

  const ids = CONTRIBUTION_QUESTION_IDS;
  const questions = context?.questions || [];
  const projects = context?.projects || [];
  const hasActiveAssignments = projects.length > 0;
  const scenario = contributionScenario(projects, today);
  const isActiveFlow = scenario === "active";
  const isEndingSoon = scenario === "ending-soon";
  const endingProjects = endingSoonProjects(projects, today);
  const endingProjectIds = new Set(endingProjects.map((project) => project.id));
  const displayedProjects = [...endingProjects, ...projects.filter((project) => !endingProjectIds.has(project.id))];
  const correctionNote = isEndingSoon ? END_DATE_NOTE : PROJECT_INFORMATION_NOTE;
  const correctionIncluded = isEndingSoon ? draft.endDateIncorrect : !hasActiveAssignments && draft.projectInformationIncorrect;
  const interest = questions.find((question) => question.id === ids.interest);
  const availability = questions.find((question) => question.id === availabilityQuestionId(scenario));
  const dateQuestion = questions.find((question) => question.id === ids.date);
  const noteQuestion = questions.find((question) => question.id === ids.note);
  const projectQuestion = questions.find((question) => question.id === ids.project);
  const interestOptions = questionOptions(interest).filter((option) => option in INTEREST_DESCRIPTIONS);
  const timingOptions = questionOptions(availability);
  const validConfiguration = (!isActiveFlow || (interest?.questionType === "RADIO" && interestOptions.length === 2 && projectQuestion?.questionType === "CHECKBOX"))
    && availability?.questionType === "RADIO" && timingOptions.length > 0
    && dateQuestion?.questionType === "DATE" && noteQuestion?.questionType === "TEXT";
  const isResponsibility = isActiveFlow && draft.interest === MORE_RESPONSIBILITY;
  const selectedProject = projects.find((project) => project.id === draft.projectId);
  const canReview = !!context && !!validConfiguration && contributionReady(draft, projects, questions, scenario);
  const reviewInterest = !isActiveFlow ? "starting your next project" : isResponsibility
    ? `taking on more responsibility on ${selectedProject?.name || "your current project"}`
    : "taking on another project";
  const reviewAvailability = draft.availability === "Now"
    ? "would be available now"
    : draft.availability === FROM_DATE
      ? `would be available from ${parseProjectDate(draft.date)?.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })}`
      : "would like to discuss your availability";

  function editAnswers() {
    setError("");
    setReviewing(false);
  }

  function changeDraft(patch: Partial<ContributionDraft>) {
    setDraft((previous) => ({ ...previous, ...patch }));
    setError("");
  }

  function startReview(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!canReview) return;
    setError("");
    setReviewing(true);
  }

  async function submit() {
    if (!canReview || !context || submitLock.current) return;
    submitLock.current = true;
    setSubmitting(true);
    setError("");
    try {
      const response = await postApi<GrowthRequest>(GROWTH_REQUESTS, getAuthToken("accessToken"), contributionPayload(context.employeeId, draft, scenario));
      const displayTitle = !isActiveFlow
        ? "Interest in your next project assignment"
        : isResponsibility
          ? `Interest in more responsibility${selectedProject ? ` on ${selectedProject.name}` : ""}`
          : "Interest in an additional project assignment";
      onSubmitted({ ...response.data, displayTitle });
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : "Could not submit your interest. Please try again.");
    } finally {
      submitLock.current = false;
      setSubmitting(false);
    }
  }

  const backNavigation = <button type="button" className={styles.moduleBack} disabled={submitting} onClick={onBack}>
    <ChevronLeft size={17} aria-hidden />Back to Growth &amp; Initiatives
  </button>;

  if (loading || loadError || !context || !validConfiguration) {
    return <>{backNavigation}<section className={styles.panel} aria-label="Contribute" aria-busy={loading}>
      <div className={styles.messageState}>
        <h1 ref={heading} tabIndex={-1}>Contribute</h1>
        {loading ? <p role="status">Loading your current projects and questions…</p>
          : loadError ? <p role="alert">{loadError}</p>
          : <p role="alert">The contribution form is currently unavailable. Please try again.</p>}
        <div className={styles.actions}>
          <Button variant="muted-outline" onClick={onBack}><ChevronLeft size={17} />Back</Button>
          {!loading ? <Button onClick={retry}>Try again</Button> : null}
        </div>
      </div>
    </section>{loading ? <Spinner variant="overlay" label="Loading your current projects and questions" /> : null}</>;
  }

  return <>{backNavigation}<section className={styles.panel} aria-labelledby={reviewing ? "contribution-review-title" : "contribution-title"}>
    {!reviewing ? <header className={styles.header}>
      <div className={styles.headerIdentity}><span className={styles.headerIcon}><BriefcaseBusiness size={22} aria-hidden /></span><span className={styles.headerCategory}>Contribute</span></div>
      <h1 id="contribution-title" ref={heading} tabIndex={-1}>{isEndingSoon ? "Ready for what’s next?" : hasActiveAssignments ? "Interested in taking on more?" : "Looking for your next project?"}</h1>
      <p className={styles.subtitle}>{isEndingSoon
        ? `${endingProjects.length} ${endingProjects.length === 1 ? "assignment is" : "projects are"} scheduled to end soon. Review the end dates in the project cards below.`
        : hasActiveAssignments ? "You’re currently assigned to these projects." : "We don’t have a current project assignment listed for you."}</p>
      {hasActiveAssignments ? <><h2 className={styles.projectHeading}>YOUR CURRENT PROJECTS</h2>
      <div className={styles.projectGrid}>
        {displayedProjects.map((project) => <div className={styles.projectCard} key={project.id}><ProjectDetails project={project} endingSoon={endingProjectIds.has(project.id)} /></div>)}
      </div></> : null}
      {!isActiveFlow ? <div className={isEndingSoon ? styles.endDateCorrection : undefined}>
        <button type="button" className={styles.projectInformationLink} aria-pressed={!!correctionIncluded} onClick={() => changeDraft(isEndingSoon ? { endDateIncorrect: true } : { projectInformationIncorrect: true })}>{correctionNote}</button>
        {correctionIncluded ? <p className={styles.projectInformationConfirmation} role="status">Thanks. We’ll include this note without changing the project record.</p> : null}
      </div> : null}
    </header> : null}

    {reviewing ? <div className={`${styles.formBody} ${styles.reviewBody}`}>
      <p className={styles.reviewEyebrow}>REVIEW</p>
      <h1 id="contribution-review-title" className={styles.reviewHeading} ref={reviewHeading} tabIndex={-1}>Here’s what you’ve shared</h1>
      <div className={styles.reviewSummary}>
        <p className={styles.summarySentence}>You’re interested in {reviewInterest} and {reviewAvailability}.</p>
        {draft.note.trim() ? <div className={styles.additionalDetails}>
          <h2>ADDITIONAL DETAILS</h2>
          <p><LinkifiedText text={draft.note.trim()} /></p>
        </div> : null}
        {correctionIncluded ? <div className={styles.additionalDetails}>
          <h2>PROJECT INFORMATION</h2>
          <p>{correctionNote}</p>
        </div> : null}
      </div>
      <button type="button" className={styles.editAnswers} disabled={submitting} onClick={editAnswers}>
        <PencilLine size={17} aria-hidden />Edit my answers
      </button>
      {error ? <p className={styles.error} role="alert">{error}</p> : null}
      <div className={styles.actions}>
        <Button className={styles.actionButton} variant="muted-outline" disabled={submitting} onClick={editAnswers}><ChevronLeft size={17} />Back</Button>
        <Button className={styles.actionButton} disabled={submitting} onClick={() => void submit()}>{submitting ? "Sharing…" : "Share My Interest"}<ArrowRight size={17} /></Button>
      </div>
    </div> : <form className={styles.formBody} onSubmit={startReview}>
      {isActiveFlow ? <fieldset className={styles.interestGroup}>
        <legend>{interest!.question}</legend>
        <div className={styles.twoColumns}>
          {interestOptions.map((option) => <Choice key={option} name="contribution-interest" value={option} checked={draft.interest === option} onChange={() => changeDraft({ interest: option, projectId: null })}>
            <span className={styles.interestCopy}><strong>{option}</strong><span>{INTEREST_DESCRIPTIONS[option]}</span></span>
          </Choice>)}
        </div>
      </fieldset> : null}

      {!isActiveFlow || draft.interest ? <div className={isActiveFlow ? styles.followUp : styles.noAssignmentFields}>
        {isResponsibility ? <fieldset className={styles.fieldGroup}>
          <legend>{projectQuestion!.question}</legend>
          <div className={styles.twoColumns}>
            {projects.map((project) => <Choice key={project.id} name="contribution-project" value={String(project.id)} checked={draft.projectId === project.id} onChange={() => changeDraft({ projectId: project.id })} project>
              <ProjectDetails project={project} />
            </Choice>)}
          </div>
        </fieldset> : null}

        <fieldset className={styles.fieldGroup} aria-describedby="contribution-timing-hint">
          <legend>{availability!.question}</legend>
          <p id="contribution-timing-hint" className={styles.hint}>Your estimate, not a confirmed allocation.</p>
          <div className={styles.timingChoices}>
            {timingOptions.map((option) => <Choice key={option} name="contribution-availability" value={option} checked={draft.availability === option} onChange={() => changeDraft({ availability: option, date: option === FROM_DATE ? draft.date : "" })} compact>{option}</Choice>)}
          </div>
        </fieldset>

        {draft.availability === FROM_DATE ? <div className={styles.dateField}>
          <label className={styles.fieldLabel} htmlFor="contribution-date">{dateQuestion!.question}</label>
          <DatePicker id="contribution-date" selected={parseProjectDate(draft.date)} onChange={(date) => changeDraft({ date: dateAnswer(date) })}
            dateFormat="MMM d, yyyy" placeholderText="Select a date" required customInput={<DatePickerFieldTrigger />} />
        </div> : null}

        <div className={styles.noteGroup}>
          <label className={styles.fieldLabel} htmlFor="contribution-note">{noteQuestion!.question} <span>Optional</span></label>
          <p id="contribution-note-hint" className={styles.hint}>You can mention the kind of work you’re interested in or anything that could help us find a suitable opportunity.</p>
          <TextArea ref={noteInput} className={styles.noteInput} id="contribution-note" rows={4} aria-describedby="contribution-note-hint" placeholder="Add a short note" value={draft.note} onChange={(event) => changeDraft({ note: event.target.value })} />
          <div className={styles.suggestions} aria-label="Note suggestions">
            {NOTE_SUGGESTIONS.map((suggestion) => <button type="button" key={suggestion} onClick={() => {
              if (!draft.note.toLowerCase().includes(suggestion.toLowerCase())) changeDraft({ note: draft.note.trim() ? `${draft.note.trim()}; ${suggestion}` : suggestion });
              noteInput.current?.focus();
            }}>{suggestion}</button>)}
          </div>
        </div>
      </div> : null}

      <div className={styles.actions}>
        <Button className={styles.actionButton} variant="muted-outline" onClick={onBack}><ChevronLeft size={17} />Back</Button>
        <Button className={styles.actionButton} type="submit" disabled={!canReview}>Review My Interest<ArrowRight size={17} /></Button>
      </div>
    </form>}
  </section>{submitting ? <Spinner variant="overlay" label="Sharing your interest" /> : null}</>;
}
